import { contentCountsUpToDate } from '../../app/actions/spaces/ContentCountsActions';
import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbHref } from '../../app/actions/UpdateBreadcrumbHref';
import { appStore } from '../../app/AppStore';
import {
  populateContentBreadcrumbs,
  watchUrlForBreadcrumbUpdates
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { IPlaceScope } from '../../app/IPlaceScope';
import { IScopeWithProps } from '../../app/IScopeWithProps';
import { updateOrderQueryParamsIfMismatched } from '../../app/sagas/spaces/content/SortContentSagas';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import {
  addUrlWatchersToScopeForExplorer,
  getContentTypesFromQueryParam
} from '../../app/spaces/Explorer/ExplorerUrlBindings';
import { SiteSpaceOwnProps } from '../../app/spaces/SiteSpace/SiteSpace';
import {
  clearAndSetFiltersFromURL,
  setURLParametersOrDefaultsForEnteringSpaceAndFetchContent,
  SiteSpaceConfigHelper
} from '../../app/spaces/SpaceConfig/SiteSpaceConfigHelper';
import { SiteSpaceContentType } from '../../app/state/SiteSpaceContentTypeState';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../app/state/SpaceInfo';
import { getQueryStringServiceFromAngular } from '../../common/react/QueryString';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { ISubplaceTabsScope } from '../helpers/SubplaceCountsHelper';
import { ContentFiltersKeys, ContentTypeFilterKey } from '../models/ContentFilters';
import { ErrorState } from '../services/ErrorState';
import { ServerService } from '../services/ServerService';
import { Telemetry } from '../services/TelemetryService';

export interface ISitePlaceScope
    extends IPlaceScope,
            ISubplaceTabsScope,
            IScopeWithProps<SiteSpaceOwnProps>
{
  flowsOnSiteEnabled: boolean;
}

export class SitePlaceCtrl {

  private currentPath: string;
  private searchParams: any;
  private errorState: ErrorState;

  public static $inject = [
    'AngularContextFactory',
    '$location',
    '$scope',
    '$state',
    'ErrorState',
    'ServerService',
    'TelemetryService',
  ];

  constructor(
    angularContextFactory: AngularContextFactory,
    private $location: ng.ILocationService,
    private $scope: ISitePlaceScope,
    private $state: ng.ui.IStateService,
    errorState: ErrorState,
    server: ServerService,
    TelemetryService: Telemetry,
  ) {
    const siteUrls = siteUrlsSelector(appStore.getState());
    populateContentBreadcrumbs([], siteUrls);

    $scope.flowsOnSiteEnabled = server.flowsOnSiteEnabled();
    this.errorState = errorState;

    $scope.props = {
      angularContext: angularContextFactory.create($scope),
      telemetry: TelemetryService,
    };

    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => {
      return this.currentPath === this.$location.path();
    });

    // Like the Explorer URL bindings, this listener dispatches an action to Redux to update the
    // current content type any time the URL path changes within the site place. This is instead
    // of the old content-type-specific subplaces that were implemented using ui-view.
    $scope.$on('$stateChangeSuccess', () => this.showContentForCurrentSubplace());

    const contentType: SiteSpaceContentType = SiteSpaceConfigHelper.contentTypeFromCurrentSitePlaceState(this.$state.current) || 'explore';

    // We need to update the current project ID in redux before the component mounts.
    // Otherwise, the sort order does not get applied properly.
    appStore.dispatch(enterSpace({
      item: SpaceItemForNonEntitySpace,
      spaceType: 'site',
      subspaceName: contentType
    }));

    const queryString = getQueryStringServiceFromAngular($location);
    this.currentPath = $location.path();
    const getPath = () => {
      return this.currentPath;
    };

    // Need to reset all the filters and sort order when navigating in to the site place
    // This does not fetch content for the site level
    setURLParametersOrDefaultsForEnteringSpaceAndFetchContent(appStore, $location);

    addUrlWatchersToScopeForExplorer({ $scope, $location, queryString, store: appStore, getPreviousPath: getPath });
  }

  private showContentForCurrentSubplace() {
    const contentType = SiteSpaceConfigHelper.contentTypeFromCurrentSitePlaceState(this.$state.current);
    if (contentType) {

      const searchFilterIsTheSame = this.searchParams && this.searchParams[ContentFiltersKeys.search] === this.$location.search()[ContentFiltersKeys.search];
      const pathChanged = this.currentPath !== this.$location.path();
      this.searchParams = this.$location.search();
      this.currentPath = this.$location.path();
      // Need to reset filters and breadcrumbs when changing the content type because the url watchers all check
      // currentPath vs. $location.path() and don't run if paths are different, but this is the function that
      // updates the currentPath... after the url watchers run. So we need to make sure the filters and breadcrumbs
      // get updated from the url. TFSID 809212
      clearAndSetFiltersFromURL(appStore, this.$location);

      // This checks for the content type query param so we can correctly set the data connection subspace
      let subspaceName = contentType;
      if (subspaceName === 'datasource') {
        const contentTypesParam = this.$location.search()[ContentTypeFilterKey];
        const contentTypes = getContentTypesFromQueryParam(contentTypesParam, []);
        if (contentTypes.length === 1 && contentTypes[0] === 'dataconnection') {
          subspaceName = 'dataconnection';
        }
      }
      if (subspaceName === 'flow') {
        if (!this.$scope.flowsOnSiteEnabled) {
          this.errorState.go({
            detailsLocKey: 'pageDoesNotExist',
            redirectState: 'main.site.content.site.projects',
            redirectLocKey: 'backToContent'
          });
        }
      }

      // TODO: Find someway to avoid dispatching occasional redundant updateBreadcrumbHref action
      // This action will sometimes be redundant with the action in watchUrlForBreadcrumbUpdates,
      // but since the watcher and the "onStateChangeSuccess" events don't always happen deterministically,
      // we need to fire this event in both places.
      appStore.dispatch(updateBreadcrumbHref(this.$location.absUrl()));

      const newSpaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'site',
        subspaceName: subspaceName
      };
      appStore.dispatch(enterSpace(newSpaceInfo));

      updateOrderQueryParamsIfMismatched([], newSpaceInfo);

      // If we just switched tabs and search filters are the same, we know content counts are up to date.
      // They will get set to false by the above EnterSpaceAction.
      if (pathChanged && searchFilterIsTheSame) {
        appStore.dispatch(contentCountsUpToDate());
      }

      appStore.dispatch(requestSpaceContents());
    }
  }

}
